import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  AdventureCardType,
  CommonCustomerItem, CommonItemProps,
  CurrentUser,
  HomePageData,
  MenuResponse,
  OgData,
  ProfileGear,
} from 'types';
import { motion } from 'framer-motion';
import { trackSessionRedirects } from 'utils/localStoreTools';
import { getHomePageLdJson } from 'utils/seoFormatters';
import { CustomerPageWrapper } from 'containers/CustomerPageWrapper';
import styles from './Home.module.scss'

import Image from 'next/legacy/image';
import { Button } from '../../components/Button';
import { getGearList } from '../../api';
import { PRReviews } from '../ProductPage/PRPostsReviews';
import { TopSellers } from '../ProductPage/TopSellers';
import { Snackbar, SnackbarProps } from '../../components/Snackbar';
import { NextLink } from '../../components/NextLink';
import { APPSTORE_APP_LINK, GOOGLE_PLAY_APP_LINK } from '../../consts';
import cx from 'classnames';
import { handleFavorite } from '../../utils/services';
import { Activities } from '../../components/Activities';

import { LikeButton } from '../../components/LottieButtons/LikeButton';

export type HomeProps = {
  menuData: MenuResponse;
  ogData?: OgData[];
  activities: CommonCustomerItem[];
  pageData: HomePageData;
  user?: CurrentUser;
  externalReviews?: any;
  topSellers?: null | any,
  topAmbassadors?: null | any,
};

export const Home = ({
  menuData = {} as MenuResponse,
  pageData,
  user,
  ogData,
  activities,
  externalReviews,
  topSellers,
  topAmbassadors,
}: HomeProps) => {
  const router = useRouter();
  const {
    badge_section: badgeSection,
    activities_section: { rent: rentActivities, sell: sellActivities },
    adventure_cards: adventureCards,
  } = pageData;
  const [snackNotification, setSnackNotification] = useState<SnackbarProps | null>(null);
  const ldJsonData = getHomePageLdJson();
  const seoHiddenProps = {
    title: 'SENDY: BUY - SELL - RENT - SEND',
    description:
      'SENDY is an online marketplace and gear hub that enables community members to Buy, sell, and Rent Adventure Sports equipment anywhere in North America. Brought to you by Adventure Sports icons Travis Rice and Cam Zink, the founding team of SENDY brings a deep understanding of the action sports community. Join us today and explore a marketplace like no other.',
    image: 'https://storage.googleapis.com/sendy-storage/static/sendy_default_banner.jpg',
  };

  useLayoutEffect(() => {
    trackSessionRedirects(router);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CustomerPageWrapper
      badge={badgeSection}
      headTitle="Home | SENDY"
      hiddenComponentProps={seoHiddenProps}
      ldJsonData={ldJsonData}
      menuData={menuData}
      ogData={ogData}
      user={user}
    >
      <div style={{ overflow: 'hidden' }}>
        <div className={cx(styles.promoBox, styles.notForMobiles)}>
          <video className={styles.promo} playsInline autoPlay muted loop>
            <source src='/promo.mp4' type='video/mp4' />
          </video>
        </div>
        <div className={cx(styles.promoBox, styles.forMobileOnly)}
             onClick={() => window.location.assign('https://onelink.to/sendy')}>
          <video className={styles.promo} playsInline autoPlay muted loop>
            <source src='/promoMob.mp4' type='video/mp4' />
          </video>
        </div>
        <Activities activities={activities} className={styles.gearActivities} />
        <WelcomeSection />
        <BroseByGearSection
          activities={sellActivities}
          title={'Browse by gear type'}
          allText={'All GEAR'}
          link={'/gear?type=sell'}
        />
        <LatestDrops />
        <BrandsSection brands={menuData.brands} />
        <AdventureCards cardData={adventureCards[0]} />
        <div className={styles.separator} />
        <RentingThroughSection />
        <div className={styles.separator} />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          {topSellers && (
            <TopSellers
              title={'Top sellers using SENDY'}
              sellers={topSellers}
              currentUser={user}
              setSnackbarMessage={setSnackNotification}
              withBorder={false}
              leftAligned
            />
          )}
        </motion.div>
        <div className={styles.separator} />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          {topAmbassadors && (
            <TopSellers
              title={'Top pros using SENDY'}
              sellers={topAmbassadors}
              ambassadors={1}
              currentUser={user}
              setSnackbarMessage={setSnackNotification}
              withBorder={false}
              leftAligned
            />
          )}
        </motion.div>

        <motion.div
          className={cx(styles.promoBoxBigger)}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <video className={styles.promo} playsInline autoPlay muted loop>
            <source src='/home/CloseTheLoop.mp4' type='video/mp4' />
          </video>
        </motion.div>
        <motion.div
          className={styles.reviewsContainer}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className={styles.reviewsTitle}>What the community is saying</div>
          <PRReviews reviews={externalReviews?.data || []} hideDivider />
        </motion.div>
        <AdventureCards cardData={adventureCards[1]} />
        <div className={styles.separator} />
        <ReadySection />
        <div className={styles.separator} />
        <AdventureCards cardData={adventureCards[2]} />
        <div className={styles.separator} />
        <BroseByGearSection
          activities={rentActivities}
          title={'Rent anything, anywhere.'}
          allText={'SEARCH ALL RENTALS'}
          link={'/gear?type=rent'}
        />
        <motion.div
          className={cx(styles.promoBox)}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <video className={styles.promo} playsInline autoPlay muted loop>
            <source src='/home/AerialLandscapes.mp4' type='video/mp4' />
          </video>
        </motion.div>

        {snackNotification && <Snackbar {...snackNotification} onClose={() => setSnackNotification(null)} />}
        {/*<GearBlock initialGear={initialGear} activities={activities} filters={undefined} appliedFilters={appliedFilters} user={user} isHome />*/}
      </div>
    </CustomerPageWrapper>
  );
};

const WelcomeSection = () => {
  const router = useRouter();
  return (
    <div className={styles.welcomeContainer}>
      <div className={styles.notForMobiles}>
        <div className={styles.background}>
          <Image
            alt='Background'
            src={'/home/heroBackDesktop.png'}
            layout={'fill'}
          />
        </div>
      </div>
      <div className={styles.forMobileOnly}>
        <div className={styles.background}>
          <Image
            alt='Background'
            src={'/home/heroBackMobile.png'}
            layout={'fill'}
          />
        </div>
      </div>

      <div className={styles.logo}>
        <Image
          alt='SENDY'
          src={'/home/logoBig.png'}
          layout={'fill'}
        />
      </div>
      <div className={styles.descriptionContainer}>
        <div className={styles.content}><span className={styles.bolder}>SENDY is a gear hub</span> where you can buy,
          sell, or rent sports equipment anytime, <span className={styles.bolder}>from anywhere.</span></div>
      </div>
      <div className={styles.storeButtons}>
        <NextLink href={APPSTORE_APP_LINK} newTab={true}>
          <Image
            alt='App Store'
            src='/home/AppStoreBadge.svg'
            width={162}
            height={48}
            layout='fixed'
            objectFit='contain'
          />
        </NextLink>
        <NextLink href={GOOGLE_PLAY_APP_LINK} newTab={true}>
          <Image
            alt='Google Pay'
            src='/home/GooglePlayBadge.svg'
            width={162}
            height={48}
            layout='fixed'
            objectFit='contain'
          />
        </NextLink>
      </div>
      <div  className={styles.heroCards}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className={styles.card}>
            <div className={styles.title}>BUY</div>
            <div className={styles.image}>
              <Image
                alt='Buy'
                src={'/home/heroBuy.png'}
                layout={'fill'}
              />
            </div>
            <div className={styles.heroContent}>
              <div className={styles.description}>Score the gear you need from fellow adventure seekers.</div>
              <Button
                action={() => router.push(`/gear?status=active&type=sell&sort=relevancy&order=desc&page=1`)}
                caption='SHOP NOW'
                buttonType='white'
              />
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className={styles.card}>
            <div className={styles.title}>SELL</div>
            <div className={styles.image}>
              <Image
                alt='Sell'
                src={'/home/heroSell.png'}
                layout={'fill'}
              />
            </div>
            <div className={styles.heroContent}>
              <div className={styles.description}>Pass your gear on to the next rider and keep the stoke going.</div>
              <Button
                action={() => router.push(`/item-management`)}
                caption='LIST YOUR GEAR'
                buttonType='white'
              />
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className={styles.card}>
            <div className={styles.title}>RENT</div>
            <div className={styles.image}>
              <Image
                alt='Rent'
                src={'/home/heroRent.png'}
                layout={'fill'}
              />
            </div>
            <div className={styles.heroContent}>
              <div className={styles.description}>Rent the equipment you need for your next adventure right here.</div>
              <Button
                action={() => router.push('/rent')}
                caption='FIND DETAILS'
                buttonType='white'
              />
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className={styles.card}>
            <div className={styles.title}>EXPLORE</div>
            <div className={styles.image}>
              <Image
                alt='Explore'
                src={'/home/heroExplore.png'}
                layout={'fill'}
              />
            </div>
            <div className={styles.heroContent}>
              <div className={styles.description}>Score the gear you need from fellow adventure seekers.</div>
              <Button
                action={() => router.push(`/explore`)}
                caption='COMING SOON'
                buttonType='white'
              />
            </div>
          </div>
        </motion.div>
      </div>

    </div>
  )
}

export const BroseByGearSection = (
  {activities, title, allText, link}
    : {activities: CommonCustomerItem[], title: string, allText: string, link: string}
) => {
  const router = useRouter();

  return (
    <motion.div
      className={styles.browseByContainer}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.1 }}
    >
      <div className={styles.browseByRow}>
        <div className={styles.browseByText}>{title}</div>
        <div onClick={() => router.push(`${link}`)} className={styles.browseByAllText}>{allText} {'>'}</div>
      </div>
      <div className={styles.activitiesRow}>
        {activities.map((activity) => (
          <div
            key={activity.id}
            className={styles.activityCard}
            onClick={() => router.push(`${link}&activities=["${activity.slug}"]`)}
          >
            <Image
              alt={activity.title}
              src={activity.logo || ''}
              layout={'fill'}
              objectFit={'cover'}
            />
            <div className={styles.activityTitle}>{activity.title}</div>
          </div>
        ))}
      </div>
    </motion.div>
  )
}

const LatestDrops = () => {
  const router = useRouter();
  const [gears, setGears] = useState<ProfileGear[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const getGears = useCallback((page) => {
    setLoading(true)
    getGearList({limit: 6, page, sort: 'posted_at', order: 'desc'})
      .then(resp => {
        if(page===1)
          setGears(resp.hits)
        else
          setGears(g => ([...g, ...resp.hits]))
        setPage(page+1)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    getGears(1)
  }, [])


  return (
    <motion.div
      className={styles.browseByContainer}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.1 }}
    >
      <div className={styles.browseByRow}>
        <div className={styles.browseByText}>Check out the latest drops!</div>
        <div onClick={() => router.push(`/gear?sort="posted_at"&order="desc"`)} className={styles.browseByAllText}>SEE ALL {'>'}</div>
      </div>
      <div className={styles.latestDrops}>
        {gears.map((gear) => (
          <GearCard gear={gear}/>
        ))}
      </div>
      <div className={styles.moreButtonContainer}>
        <Button
          disabled={loading}
          action={() => getGears(page)}
          caption={'SHOW MORE'}
          shape={'slim'}
        />
      </div>
    </motion.div>
  )
}

export const GearCard = ({gear, smaller} : {gear: ProfileGear, smaller?: boolean}) => {
  const router = useRouter();
  const [fav, setFav] = useState(gear.in_my_favorites);

  const toggleFavorite = useCallback((slug: string, isFavorite: boolean) => {
    handleFavorite(slug, isFavorite, () => {})
      .then((isSuccess) => {
        if(isSuccess)
          setFav(!isFavorite)
      })
  }, []);

  return (
    <motion.div
      className={styles.latestGearCard}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.1 }}
    >
      <div className={styles.latestGearImage}>
        <Image
          alt={gear.title}
          src={gear.cover_image}
          layout={'fill'}
          objectFit={'cover'}
        />
        <div className={styles.latestType} style={{ backgroundColor: gear.type === 'rent' ? '#3D4B54' : '#6CD4AF' }}>
          <div className={styles.latestTypeText} style={{ color: gear.type === 'rent' ? '#fff' : '#3D4B54' }}>{gear.type === 'rent' ? 'rent' : 'buy'}</div>
        </div>
      </div>
      <div className={styles.latestTexts}>
        <div className={styles.latestUser}>{gear.user?.username}</div>
        <div className={styles.row}>
          <div className={styles.latestTitle}>{gear.title}</div>
          <LikeButton
            isFav={fav}
            onPress={(fav) => {
              toggleFavorite(gear.slug, fav)
            }}
          />
        </div>
        <div className={styles.latestTitle}>
          <div className={styles.price}>${gear.price}{gear.type === 'rent' ? '/day' : ''}{'  '}</div>
          {!!gear.original_price && <div className={styles.crossed}>${gear.original_price}</div>}
        </div>
      </div>
      <Button
        action={() => router.push(`/gear/${gear.slug}`)}
        caption={'CHECK IT OUT'}
        buttonType={'whiteBlue'}
      />
    </motion.div>
  )
}

const BrandsSection = ({brands} : {brands: CommonItemProps[]}) => {
  const router = useRouter();

  return (
    <motion.div
      className={styles.browseByContainer}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.1 }}
    >
      <div className={styles.browseByRow}>
        <div className={styles.browseByText}>Browse by brand</div>
        <div onClick={() => router.push(`/gear?type=sell`)} className={styles.browseByAllText}>ALL BRANDS {'>'}</div>
      </div>
      <div className={styles.brandsRow}>
        {brands.map(brand => (
          <div
            key={brand.id}
            className={styles.brandCard}
            onClick={() => router.push(`/gear?type=sell&brands=["${brand.slug}"]`)}
          >
            <Image
              alt={brand.title}
              src={brand.logo || ''}
              layout={'fill'}
              objectFit={'cover'}
            />
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export const AdventureCards = ({ cardData }: { cardData: AdventureCardType }) => {
  const router = useRouter();

  const cardsFirst: CommonCustomerItem[] = [cardData.categories[0]];
  // @ts-ignore
  const cardsLast: CommonCustomerItem[] = cardData.categories.map((card, index) => {
    if (index !== 0)
      return card
  }).filter(i => !!i)

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.1 }}
    >
      <div className={cx(styles.browseByContainer, styles.notForMobiles)}>
        <div className={styles.browseByRow}>
          <div>
            <div className={styles.browseByText}>{cardData.title}</div>
            <div className={styles.browseBySubtitle}>{cardData.subtitle}</div>
          </div>
          <div onClick={() => router.push(`/gear?activities=["${cardData.activity.slug}"]`)} className={styles.browseByAllText}>SHOP ALL {cardData.cta_text} {'>'}</div>
        </div>
        <div className={styles.readyRow}>
          {cardData.categories.map(item => (
            <div key={item.id} className={styles.categoryCard}>
              <div className={styles.categoryImage}>
                <Image
                  alt={item.title}
                  src={item.logo || ''}
                  layout={'fill'}
                  objectFit={'cover'}
                />
              </div>
              <Button
                action={() => router.push(`/gear?type=sell&category=${item.slug}`)}
                caption={`${item.title?.toUpperCase()}`}
                iconSrcRight={'/icons/arrows/ArrowRightDarkGray.svg'}
                buttonType='white'
              />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.forMobileOnly}>
        <div className={styles.browseByContainer} style={{paddingBottom: 0, marginBottom: 0}}>
          <div className={styles.browseByRow}>
            <div>
              <div className={styles.browseByText}>{cardData.title}</div>
              <div className={styles.browseBySubtitle}>{cardData.subtitle}</div>
            </div>
          </div>
        </div>

        <div className={styles.browseByContainerMobile} style={{padding: 0}}>
          {cardsFirst.map(item => (
            <div key={item.id} className={styles.categoryCard}>
              <div className={styles.categoryImage}>
                <Image
                  alt={item.title}
                  src={item.logo || ''}
                  layout={'fill'}
                  objectFit={'cover'}
                />
              </div>
              <Button
                action={() => router.push(`/gear?type=sell&category=${item.slug}`)}
                caption={`${item.title?.toUpperCase()}`}
                iconSrcRight={'/icons/arrows/ArrowRightDarkGray.svg'}
                buttonType='white'
              />
            </div>
          ))}
          <div onClick={() => router.push(`/gear?activities=["${cardData.activity.slug}"]`)} className={styles.categoryPseudoCard}>
            <div className={styles.text}>SHOP ALL {cardData.cta_text} {'>'}</div>
          </div>
          {cardsLast.map(item => (
            <div key={item.id} className={styles.categoryCard}>
              <div className={styles.categoryImage}>
              <Image
                  alt={item.title}
                  src={item.logo || ''}
                  layout={'fill'}
                  objectFit={'cover'}
                />
              </div>
              <Button
                action={() => router.push(`/gear?type=sell&category=${item.slug}`)}
                caption={`${item.title?.toUpperCase()}`}
                iconSrcRight={'/icons/arrows/ArrowRightDarkGray.svg'}
                buttonType='white'
              />
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

const readyData = [
  {
    title: '1. CREATE YOUR LISTINGS',
    description: <div>Upload photos, set your price, <span className={styles.semiBold}>pick a shipping box.</span>
    </div>,
    descriptionMob: 'Upload photos, set your price, pick a box, and print your label.',
    src: '/home/readyFirst.png',
  },
  {
    title: '2. GO LIVE NATIONWIDE',
    description: <div>Your gear, now posted on SENDY is available <span className={styles.semiBold}>nationwide.</span></div>,
    descriptionMob: 'Your gear’s live on SENDY — buyers nationwide can find it.',
    src: '/home/readySecond.png'
  },
  {
    title: '3. SHIP OR MEET & GET PAID',
    description: <div>Pack it up, print out your <span className={styles.semiBold}>SENDY provided label,</span> send it out, and get paid once it’s confirmed delivered.</div>,
    descriptionMob: 'Pack it up, send it out, and get paid once it’s confirmed delivered.',
    src: '/home/readyThird.png'
  },
]

export const ReadySection = () => {
  const router = useRouter();

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.1 }}
    >
      <div className={cx(styles.browseByContainer, styles.notForMobiles)}>
        <div className={styles.browseByRow}>
          <div className={styles.browseByText}>Ready to sell or rent your loved gear? Here’s how -</div>
          <div onClick={() => router.push(`/gear`)} className={styles.browseByAllText}>CHECK IT OUT {'>'}</div>
        </div>
        <div className={styles.readyRow}>
          {readyData.map(item => (
            <div className={styles.readyCard}>
              <div className={styles.readyTitle}>{item.title}</div>
              <div className={styles.readyImage}>
                <Image
                  alt={item.title}
                  src={item.src}
                  layout={'fill'}
                  objectFit={'cover'}
                />
              </div>
              <div className={styles.readyDescription}>{item.description}</div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.forMobileOnly}>
        <div className={styles.browseByContainerMobile}>
          <div className={styles.readyCard}>
            <div className={styles.pseudoCard}>
              <div>
                <div className={styles.browseByText}>Ready to sell or rent your loved gear? Here’s how -</div>
                <div onClick={() => router.push('/rent')} className={styles.browseByDescription}>SENDY’S SECURITY {'>'}</div>
              </div>
              <div onClick={() => router.push(`/gear`)} className={styles.browseByAllText}>CHECK IT OUT {'>'}</div>
            </div>
          </div>
          {readyData.map(item => (
            <div className={styles.readyCard}>
              <div className={styles.readyTitle}>{item.title}</div>
              <div className={styles.readyImage}>
              <Image
                  alt={item.title}
                  src={item.src}
                  layout={'fill'}
                  objectFit={'cover'}
                />
              </div>
              <div className={styles.readyDescription}>{item.descriptionMob}</div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

const rentingData = [
  {
    title: '1. LOCK IT IN',
    description: <div>Reserve your dates, pay the rental fee and refundable deposit <span className={styles.semiBold}>all on SENDY!</span></div>,
    descriptionMob: 'Reserve your dates, pay the rental fee and refundable deposit all on SENDY!',
    src: '/home/rentFirst.png',
  },
  {
    title: '2. MEET UP & GEAR UP',
    description: <div>Meet the owner at the agreed upon pickup spot - <span className={styles.semiBold}>grab your gear, and roll.</span></div>,
    descriptionMob: 'Meet the owner at the agreed upon pickup spot -grab your gear, and roll.',
    src: '/home/rentSecond.png',
  },
  {
    title: '3. RIDE HARD, RETURN EASY',
    description: <div>Send it, <span className={styles.semiBold}>return the gear,</span> and get your deposit back once it checks out.</div>,
    descriptionMob: 'Send it, return the gear, and get your deposit back once it checks out.',
    src: '/home/rentThird.png',
  },
];

export const RentingThroughSection = () => {
  const router = useRouter();

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.1 }}
    >
      <div className={cx(styles.browseByContainer, styles.notForMobiles)}>
        <div className={styles.browseByRow}>
          <div>
            <div className={styles.browseByText}>Renting through SENDY is easy -</div>
            <div onClick={() => router.push('/rent')} className={styles.browseByDescription}>SENDY’S SECURITY {'>'}</div>
          </div>
          <div onClick={() => router.push(`/gear?type='rent'`)} className={styles.browseByAllText}>SEARCH ALL RENTALS {'>'}</div>
        </div>
        <div className={styles.readyRow} style={{ backgroundColor: '#fff' }}>
          {rentingData.map(item => (
            <div className={styles.readyCard}>
              <div className={styles.readyTitle}>{item.title}</div>
              <div className={styles.readyImage}>
                <Image
                  alt={item.title}
                  src={item.src}
                  layout={'fill'}
                  objectFit={'cover'}
                />
              </div>
              <div className={styles.readyDescription}>{item.description}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.forMobileOnly}>
        <div className={styles.browseByContainerMobile}>
          <div className={styles.readyCard}>
            <div className={styles.pseudoCard}>
              <div>
                <div className={styles.browseByText}>Renting through SENDY is easy -</div>
                <div onClick={() => router.push('/rent')} className={styles.browseByDescription}>SENDY’S SECURITY {'>'}</div>
              </div>
              <div onClick={() => router.push(`/gear?type='rent'`)} className={styles.browseByAllText}>SEARCH ALL RENTALS {'>'}</div>
            </div>
          </div>
          {rentingData.map(item => (
            <div className={styles.readyCard}>
              <div className={styles.readyTitle}>{item.title}</div>
              <div className={styles.readyImage}>
                <Image
                  alt={item.title}
                  src={item.src}
                  layout={'fill'}
                  objectFit={'cover'}
                />
              </div>
              <div className={styles.readyDescription}>{item.descriptionMob}</div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>

  )
}
